import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Square, Send, Trash2, Check, ChevronDown, ChevronUp, MessageSquareShare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import api, { aufgabenAPI } from '@/lib/api';

/* Übergabe-Feld (Betreiber 12.10.2026: „eine Übergabe-Funktion aus meinem Bereich für Cagla in einem Kommunikationsfeld
   mit Erledigt bei Cagla"). Ein Verlauf wie in einem Chat: Die Leitung schreibt oder spricht hinein, die Empfängerin sieht
   die Übergabe bei sich ganz oben, hört die Aufnahme, hakt „Erledigt" ab und meldet zurück. Beide sehen denselben Verlauf. */

const MAX_SEKUNDEN = 170;
const zeit = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const feld = 'w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

const wannText = (iso) => iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const vorname = (n) => (n || '').split(' ')[0] || 'Team';

function Eintrag({ e, onGeaendert }) {
  const navigate = useNavigate();
  const [antwort, setAntwort] = useState('');
  const [busy, setBusy] = useState(false);
  const [posten, setPosten] = useState(e.posten || []);
  const [listeOffen, setListeOffen] = useState(false);
  const offen = e.status === 'offen';
  const erledigen = async () => {
    setBusy(true);
    try { await aufgabenAPI.erledigt(e.id, antwort.trim() || undefined); toast.success('Erledigt. Der Absender bekommt eine Meldung.'); onGeaendert?.(); }
    catch (x) { toast.error(x?.response?.data?.detail || 'Nicht gespeichert'); }
    setBusy(false);
  };
  const haken = async (p) => {
    const neu = !p.erledigt_am;
    setPosten(l => l.map(x => x.nr === p.nr ? { ...x, erledigt_am: neu ? new Date().toISOString() : null } : x));
    try { const r = (await aufgabenAPI.postenHaken(e.id, p.nr, neu)).data; if (r.status === 'erledigt') onGeaendert?.(); }
    catch (x) { setPosten(e.posten || []); toast.error(x?.response?.data?.detail || 'Nicht gespeichert'); }
  };
  return (
    <li className={`rounded-2xl border p-3 ${offen ? (e.fuer_mich ? 'border-primary/40 bg-primary/5' : 'border-border bg-card') : 'border-border/60 bg-muted/40'}`} data-testid={`uebergabe-${e.id}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-bold text-foreground">{e.von_mir ? 'Du' : vorname(e.von)} an {e.fuer_mich ? 'dich' : vorname(e.an)}</span>
        <span className="text-muted-foreground tabular-nums">{wannText(e.am)}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold ${offen ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'}`}>
          {offen ? 'offen' : `erledigt von ${vorname(e.erledigt_von)} · ${wannText(e.erledigt_am)}`}
        </span>
      </div>
      {e.audio && <div className="mt-2"><UebergabeAudio id={e.id} /></div>}
      {e.text && <p className={`mt-2 whitespace-pre-wrap text-sm leading-relaxed ${offen ? '' : 'text-muted-foreground'}`}>{e.text}</p>}
      {posten.length > 0 && (
        <div className="mt-2">
          <button type="button" onClick={() => setListeOffen(o => !o)} aria-expanded={listeOffen} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-semibold hover:bg-accent">
            {listeOffen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} {posten.length} mitgegebene Aufgaben · {posten.filter(p => p.erledigt_am).length} erledigt
          </button>
          {listeOffen && (
            <ol className="mt-2 divide-y divide-border/50 rounded-xl border border-border bg-card">
              {posten.map(p => (
                <li key={p.nr} className="flex items-center gap-3 px-3 py-1.5">
                  <input type="checkbox" className="w-4 h-4 accent-marke flex-none" checked={!!p.erledigt_am} disabled={!offen || !e.fuer_mich} onChange={() => haken(p)} aria-label={`${p.text} erledigt`} />
                  <span className="min-w-0 flex-1">
                    {p.link ? <button type="button" onClick={() => navigate(p.link)} className={`text-left text-sm hover:underline ${p.erledigt_am ? 'text-muted-foreground line-through' : 'text-primary'}`}>{p.text}</button>
                      : <span className={`text-sm ${p.erledigt_am ? 'text-muted-foreground line-through' : ''}`}>{p.text}</span>}
                    {p.detail && <span className="block truncate text-xs text-muted-foreground">{p.detail}</span>}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      {!offen && e.rueckmeldung && e.rueckmeldung !== 'alle Posten erledigt' && (
        <p className="mt-2 rounded-xl bg-card px-3 py-2 text-sm"><b className="text-xs">{vorname(e.erledigt_von)}:</b> {e.rueckmeldung}</p>
      )}
      {offen && e.fuer_mich && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input id={`uebergabe-antwort-${e.id}`} value={antwort} onChange={x => setAntwort(x.target.value)} maxLength={500} placeholder="Rückmeldung (optional)"
            className="h-9 min-w-[180px] flex-1 rounded-xl border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          <Button size="sm" disabled={busy} onClick={erledigen} data-testid={`uebergabe-erledigt-${e.id}`}><Check className="w-4 h-4" /> Erledigt</Button>
        </div>
      )}
    </li>
  );
}

export default function UebergabeFeld({ nutzer, ich, eigene, onGeaendert }) {
  const [d, setD] = useState(null);
  const [an, setAn] = useState('');
  const [text, setText] = useState('');
  const [blob, setBlob] = useState(null);
  const [vorschau, setVorschau] = useState(null);
  const [nimmtAuf, setNimmtAuf] = useState(false);
  const [sekunden, setSekunden] = useState(0);
  const [wahl, setWahl] = useState({});
  const [busy, setBusy] = useState(false);
  const [alleZeigen, setAlleZeigen] = useState(false);
  const recorder = useRef(null), teile = useRef([]), timer = useRef(null);
  const andere = (nutzer || []).filter(u => u.user_id !== ich);

  const laden = () => api.get('/aufgaben/uebergaben').then(r => setD(r.data)).catch(() => setD(false));
  useEffect(() => { laden(); const t = setInterval(laden, 60000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!an && andere.length) setAn((andere.find(u => /cagla/i.test(u.name)) || andere[0]).user_id); }, [an, andere]);
  const stopp = () => { try { if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop(); } catch { /* egal */ } };
  useEffect(() => () => { clearInterval(timer.current); stopp(); }, []);
  useEffect(() => { if (!blob) { setVorschau(null); return undefined; } const u = URL.createObjectURL(blob); setVorschau(u); return () => URL.revokeObjectURL(u); }, [blob]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { toast.error('Aufnahme in diesem Browser nicht möglich. Bitte tippen.'); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const typ = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find(t => MediaRecorder.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined);
      teile.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) teile.current.push(e.data); };
      rec.onstop = () => {
        clearInterval(timer.current); setNimmtAuf(false);
        stream.getTracks().forEach(t => t.stop());
        const b = new Blob(teile.current, { type: (rec.mimeType || 'audio/webm').split(';')[0] });
        if (b.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        setBlob(b);
      };
      recorder.current = rec; rec.start(1000);
      setBlob(null); setNimmtAuf(true); setSekunden(0);
      timer.current = setInterval(() => setSekunden(s => { if (s + 1 >= MAX_SEKUNDEN) stopp(); return s + 1; }), 1000);
    } catch { toast.error('Mikrofon nicht freigegeben'); }
  };

  const gewaehlt = Object.keys(wahl).filter(k => wahl[k]);
  const senden = async () => {
    if (!an) { toast.error('Bitte eine Person wählen'); return; }
    if (!blob && !text.trim() && !gewaehlt.length) { toast.error('Bitte sprechen, schreiben oder Aufgaben mitgeben'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('an', an); fd.append('text', text.trim()); fd.append('aufgaben_ids', gewaehlt.join(','));
      if (blob) fd.append('audio', new File([blob], `uebergabe.${blob.type.includes('mp4') ? 'm4a' : blob.type.includes('ogg') ? 'ogg' : 'webm'}`, { type: blob.type }));
      const r = (await api.post('/aufgaben/uebergabe', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 150000 })).data;
      toast.success(r.uebergabe?.transkript_fehlt ? 'Übergeben. Das Transkript fehlt, die Aufnahme ist dabei.' : 'Übergeben.');
      setText(''); setBlob(null); setWahl({}); laden(); onGeaendert?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Übergabe nicht gesendet'); }
    setBusy(false);
  };

  if (!d) return null;
  const darfSenden = d.darf_senden && andere.length > 0;
  if (!darfSenden && !d.eintraege.length) return null;          // wer nichts senden darf und nichts bekommen hat, sieht kein leeres Feld
  const offene = d.eintraege.filter(e => e.status === 'offen');
  const erledigte = d.eintraege.filter(e => e.status !== 'offen');
  const gezeigt = [...offene, ...(alleZeigen ? erledigte : erledigte.slice(0, 2))];
  const geaendert = () => { laden(); onGeaendert?.(); };

  return (
    <Card data-testid="uebergabe-feld" className={d.offen_fuer_mich ? 'border-primary/40 ring-1 ring-primary/20' : ''}>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-bold flex items-center gap-1.5"><MessageSquareShare className="w-4 h-4 text-primary" /> Übergabe</h2>
          {d.offen_fuer_mich > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">{d.offen_fuer_mich} offen für dich</span>}
          {!d.offen_fuer_mich && offene.length > 0 && <span className="text-xs text-muted-foreground">{offene.length} offen</span>}
        </div>

        {gezeigt.length > 0 && <ul className="space-y-2">{gezeigt.map(e => <Eintrag key={`${e.id}-${e.status}`} e={e} onGeaendert={geaendert} />)}</ul>}
        {erledigte.length > 2 && (
          <button className="text-xs text-muted-foreground hover:underline" onClick={() => setAlleZeigen(a => !a)}>{alleZeigen ? 'weniger zeigen' : `${erledigte.length - 2} ältere erledigte zeigen`}</button>
        )}
        {!gezeigt.length && <p className="text-sm text-muted-foreground">Noch keine Übergabe. Schreib oder sprich unten hinein.</p>}

        {darfSenden && (
          <div className="rounded-2xl border border-border bg-background p-3 space-y-2" data-testid="uebergabe-schreiben">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="uebergabe-an" className="text-xs font-semibold text-muted-foreground">An</label>
              <select id="uebergabe-an" value={an} onChange={e => setAn(e.target.value)} className="h-8 rounded-lg border border-input bg-card px-2 text-sm">
                {andere.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
              {nimmtAuf
                ? <Button size="sm" variant="outline" onClick={stopp} data-testid="uebergabe-stopp"><Square className="w-4 h-4" /> Stopp · <span className="tabular-nums">{zeit(sekunden)}</span></Button>
                : <Button size="sm" variant="outline" onClick={start} data-testid="uebergabe-aufnehmen"><Mic className="w-4 h-4" /> {blob ? 'Neu aufnehmen' : 'Sprachnachricht'}</Button>}
              {nimmtAuf && <span className="inline-flex items-center gap-1.5 text-xs text-primary"><span className="h-2 w-2 rounded-full bg-primary animate-pulse" />Aufnahme läuft</span>}
              {blob && !nimmtAuf && <button className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive" onClick={() => setBlob(null)}><Trash2 className="w-3 h-3" /> verwerfen</button>}
            </div>
            {vorschau && <audio controls src={vorschau} className="w-full" />}
            <div className="flex items-end gap-2">
              <textarea id="uebergabe-text" value={text} onChange={e => setText(e.target.value)} rows={2} maxLength={2000} className={feld}
                placeholder={blob ? 'Leer lassen: dann gilt der gesprochene Text.' : 'Was soll sie wissen und tun?'} />
              <Button onClick={senden} disabled={busy || nimmtAuf} data-testid="uebergabe-senden"><Send className="w-4 h-4" /> {busy ? 'Sendet …' : 'Übergeben'}</Button>
            </div>
            {eigene?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer font-semibold text-muted-foreground">Meine offenen Aufgaben mitgeben ({gewaehlt.length} von {eigene.length} gewählt)</summary>
                <div className="mt-2 flex gap-3">
                  <button className="text-primary hover:underline" onClick={() => setWahl(Object.fromEntries(eigene.map(e => [e.id, true])))}>alle wählen</button>
                  <button className="text-muted-foreground hover:underline" onClick={() => setWahl({})}>keine</button>
                </div>
                <ul className="mt-2 max-h-48 overflow-y-auto divide-y divide-border/50">
                  {eigene.map(e => (
                    <li key={e.id}>
                      <label className="flex cursor-pointer items-start gap-2 py-1.5">
                        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-marke" checked={!!wahl[e.id]} onChange={ev => setWahl({ ...wahl, [e.id]: ev.target.checked })} />
                        <span className="min-w-0"><span className="block truncate text-sm">{e.titel}</span><span className="block text-muted-foreground">{e.gruppe}</span></span>
                      </label>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* Die Aufnahme zum Anhören: geholt mit der Anmeldung, nie über eine offene Adresse */
export function UebergabeAudio({ id }) {
  const [url, setUrl] = useState(null);
  const [fehlt, setFehlt] = useState(false);
  useEffect(() => {
    let u = null, weg = false;
    api.get(`/aufgaben/uebergabe/${id}/audio`, { responseType: 'blob' })
      .then(r => { if (weg) return; u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => setFehlt(true));
    return () => { weg = true; if (u) URL.revokeObjectURL(u); };
  }, [id]);
  if (fehlt) return <p className="text-xs text-muted-foreground">Die Aufnahme ist nicht abrufbar.</p>;
  return url ? <audio controls src={url} className="w-full" data-testid={`uebergabe-audio-${id}`} /> : <p className="text-xs text-muted-foreground">Aufnahme wird geladen …</p>;
}
